import { useEffect, useRef, useState } from "react";
import { normalizeError } from "../operationFeedback/normalizeError";

// Load without side effects. Only the synchronous apply callback may replace
// the snapshot, after checking that this editor is still pristine.
export function useCleanRecordRefresh({ identity, version, changed, revision, blocked, load, canRefresh, acknowledge }) {
  const latest = useRef();
  latest.current = { identity, version, revision, blocked, canRefresh, acknowledge };
  const loader = useRef(load);
  loader.current = load;
  const [interaction, setInteraction] = useState(0);
  const inputEpoch = useRef(0);
  const [problem, setProblem] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    // Also catches editor input before its debounced dirty state reaches React.
    const invalidate = () => { inputEpoch.current++; setInteraction(n => n + 1); };
    const events = ["input", "change", "pointerdown", "keydown"];
    events.forEach(event => document.addEventListener(event, invalidate, true));
    return () => {
      events.forEach(event => document.removeEventListener(event, invalidate, true));
    };
  }, []);
  // A failed refresh must not stick for the rest of the page view. The next
  // remote revision may try again; the same failure does not retry by itself.
  useEffect(() => { setProblem(null); }, [identity, revision]);
  useEffect(() => {
    let active = true;
    const epoch = inputEpoch.current;
    const safe = () => active && epoch === inputEpoch.current && latest.current.identity === identity && latest.current.version === version && latest.current.revision === revision && !latest.current.blocked && latest.current.canRefresh?.() !== false;
    if (!changed || blocked || !loader.current || problem) return;
    const timer = setTimeout(async () => {
      if (!safe()) return;
      setRefreshing(true);
      try {
        const snapshot = await loader.current();
        if (!safe()) return;
        if (typeof snapshot?.apply !== "function") throw new Error("Invalid refresh snapshot");
        snapshot.apply();
        latest.current.acknowledge?.(revision);
      } catch (error) {
        if (safe()) setProblem(normalizeError(error, { operationKey: "refresh_collaboration_record" }));
      } finally {
        if (active) setRefreshing(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(timer); setRefreshing(false); };
  }, [identity, version, changed, revision, blocked, interaction, problem]);
  return { problem, refreshing };
}
